import React, { useState } from "react";
import MuiTextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import { Field } from "react-final-form";

interface TextArrayFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  addText?: string;
  [key: string]: any;
}

function ensureStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string");
  }
  return [];
}

export function TextArrayField({
  name,
  label,
  placeholder,
  disabled,
  helperText,
  fullWidth,
  ...rest
}: TextArrayFieldProps) {
  const [inputValue, setInputValue] = useState("");

  return (
    <Field name={name} parse={ensureStringArray} defaultValue={[]}>
      {({ input, meta }) => {
        const value = ensureStringArray(input.value);
        const hasError =
          (meta.touched && meta.error) || (meta.submitError && meta.touched);

        const handleAdd = () => {
          const trimmed = inputValue.trim();
          if (!trimmed || value.includes(trimmed)) return;
          setInputValue("");
          input.onChange([...value, trimmed]);
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleAdd();
          }
        };

        const handleRemove = (index: number) => {
          input.onChange(value.filter((_, i) => i !== index));
        };

        const trimmed = inputValue.trim();
        const canAdd =
          trimmed.length > 0 && !value.includes(trimmed) && !disabled;

        return (
          <FormControl
            fullWidth={fullWidth}
            disabled={disabled}
            error={Boolean(hasError)}
            variant="outlined"
            sx={{ display: "block" }}
          >
            <MuiTextField
              {...rest}
              required={false}
              label={label}
              fullWidth
              placeholder={placeholder}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              variant="outlined"
              InputProps={{
                endAdornment: canAdd ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Добавить"
                      disabled={!canAdd}
                      onClick={handleAdd}
                    >
                      <AddIcon />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            {value.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 1,
                }}
              >
                {value.map((item, index) => (
                  <Chip
                    key={`${item}-${index}`}
                    label={item}
                    onDelete={disabled ? undefined : () => handleRemove(index)}
                    aria-label={`Удалить ${item}`}
                  />
                ))}
              </Box>
            )}
            {(helperText ||
              (meta.touched && (meta.error || meta.submitError))) && (
              <FormHelperText>
                {(meta.touched && (meta.error || meta.submitError)) ||
                  helperText}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
    </Field>
  );
}
